import { useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { formatAgo } from '../lib/format'
import { pagePath } from '../lib/pages'
import { useAenderungen } from '../lib/stream'
import type { Topology } from '../lib/types'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { StatusBadge } from '../components/Status'
import { kartenZustand, TopologieKarte } from '../components/TopologieKarte'

/** Höchstens so oft frischt ein Hinweis aus dem Datenstrom die Karte auf. */
const AUFFRISCHEN_MS = 10_000

/** Nur die Geräte eines Standorts und die Leitungen, die bei ihnen beginnen. */
export function nurStandort(topo: Topology, standort: string): Topology {
  if (!standort) return topo
  const knoten = topo.knoten.filter((k) => k.standort === standort)
  const ids = new Set(knoten.map((k) => k.id))
  return {
    knoten: knoten.map((k) => (k.uebergeordnet && !ids.has(k.uebergeordnet) ? { ...k, uebergeordnet: undefined } : k)),
    kanten: topo.kanten.filter((k) => ids.has(k.von) && (!k.verwaltet || !k.zu || ids.has(k.zu))),
  }
}

interface Props {
  customerSlug: string | null
}

/**
 * Topologie aus LLDP.
 *
 * Gezeichnet wird, was die Geräte selbst melden. Ein Nachbar, den die
 * Netzwarte nicht kennt, steht bewusst mit drin: ein unbekannter Switch im
 * Rack ist genau das, was man sehen will.
 */
export function TopologiePage({ customerSlug }: Props) {
  const queryClient = useQueryClient()
  const [standort, setStandort] = useState('')
  const [nurAlarme, setNurAlarme] = useState(false)
  const {
    data: alles,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['topologie', customerSlug],
    queryFn: ({ signal }) => apiGet<Topology>('/topologie', signal),
    refetchInterval: 30_000,
  })
  // Meldet ein Gerät etwas, kann sich ein Alarm geändert haben. Viele Geräte
  // melden im Sekundentakt; die Karte holt höchstens alle zehn Sekunden nach.
  const zuletzt = useRef(0)
  useAenderungen(() => {
    const jetzt = Date.now()
    if (jetzt - zuletzt.current < AUFFRISCHEN_MS) return
    zuletzt.current = jetzt
    void queryClient.invalidateQueries({
      queryKey: ['topologie', customerSlug],
    })
  })

  const standorte = useMemo(
    () => [...new Set((alles?.knoten ?? []).map((k) => k.standort))].sort((a, b) => a.localeCompare(b)),
    [alles],
  )
  const data = useMemo(() => (alles ? nurStandort(alles, standort) : undefined), [alles, standort])

  if (isError) return <ErrorBox error={error} what="Die Topologie" />
  if (isPending || !data) return <Loading what="Topologie" />

  const byId = new Map(data.knoten.map((n) => [n.id, n]))
  const unmanaged = data.kanten.filter((k) => !k.verwaltet)
  const mitAlarm = data.knoten.filter((k) => (k.alarme ?? 0) > 0).length

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Topologie</h1>
          <div className="page-sub">
            {customerSlug ?? 'Alle Kunden'} · {data.knoten.length} Geräte, {data.kanten.length} Nachbarschaften aus LLDP
            · {mitAlarm === 0 ? 'kein Gerät' : mitAlarm === 1 ? 'ein Gerät' : `${mitAlarm} Geräte`} mit offenem Alarm
          </div>
        </div>
      </div>

      {data.knoten.length > 0 || standort ? (
        <section className="card">
          <div className="card-head" style={{ flexWrap: 'wrap', gap: 12 }}>
            <h2 className="card-title">Karte</h2>
            <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
              {standorte.length > 1 || standort ? (
                <div className="row" style={{ gap: 8 }}>
                  <label htmlFor="topo-standort" className="muted" style={{ fontSize: 13 }}>
                    Standort
                  </label>
                  <select
                    id="topo-standort"
                    className="input"
                    value={standort}
                    onChange={(e) => setStandort(e.target.value)}
                  >
                    <option value="">Alle Standorte</option>
                    {standorte.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <label className="row" style={{ gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={nurAlarme} onChange={(e) => setNurAlarme(e.target.checked)} />
                Nur Geräte mit Alarm hervorheben
              </label>
            </div>
          </div>
          <div className="card-body">
            <TopologieKarte topologie={data} customerSlug={customerSlug} nurAlarme={nurAlarme} />
          </div>
        </section>
      ) : null}

      {data.kanten.length === 0 ? (
        <EmptyState
          title="Noch keine Nachbarschaften"
          hint="LLDP wird alle zehn Minuten gelesen. Geräte ohne LLDP oder ohne SNMP-Zugang erscheinen hier nicht."
        />
      ) : (
        <section className="card">
          <div className="card-head">
            <h2 className="card-title">Verbindungen</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              {unmanaged.length} Nachbarn sind hier nicht verwaltet
            </span>
          </div>
          <table className="data">
            <thead>
              <tr>
                <th>Gerät</th>
                <th>Port</th>
                <th>Nachbar</th>
                <th>Dessen Port</th>
                <th>Zuletzt gesehen</th>
              </tr>
            </thead>
            <tbody>
              {data.kanten.map((k, i) => {
                const from = byId.get(k.von)
                return (
                  <tr key={`${k.von}-${k.von_port}-${i}`}>
                    <td>
                      <Link to={`${pagePath('geraete', from?.kunde ?? customerSlug)}/${k.von}`}>
                        {from?.name ?? k.von}
                      </Link>
                    </td>
                    <td className="mono">{k.von_port}</td>
                    <td>
                      {k.verwaltet && k.zu ? (
                        <Link to={`${pagePath('geraete', byId.get(k.zu)?.kunde ?? customerSlug)}/${k.zu}`}>
                          {k.zu_name}
                        </Link>
                      ) : (
                        <>
                          {k.zu_name}{' '}
                          <span className="badge badge-off" title="Nicht in der Netzwarte">
                            unbekannt
                          </span>
                        </>
                      )}
                    </td>
                    <td className="mono">{k.zu_port}</td>
                    <td>{formatAgo(k.zuletzt_gesehen)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </section>
      )}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Geräte</h2>
        </div>
        <table className="data">
          <thead>
            <tr>
              <th>Name</th>
              <th>Art</th>
              <th>Standort</th>
              <th>Zustand</th>
              <th>Nachbarn</th>
            </tr>
          </thead>
          <tbody>
            {data.knoten.map((n) => (
              <tr key={n.id}>
                <td>
                  <Link to={`${pagePath('geraete', n.kunde ?? customerSlug)}/${n.id}`}>{n.name}</Link>
                </td>
                <td>{n.typ}</td>
                <td>{n.standort}</td>
                <td>
                  <ZustandsBadge knoten={n} />
                </td>
                <td>{data.kanten.filter((k) => k.von === n.id || k.zu === n.id).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  )
}

function ZustandsBadge({ knoten }: { knoten: Topology['knoten'][number] }) {
  const z = kartenZustand(knoten)
  if (z.stufe === 'ok') return <StatusBadge state="ok" label="Erreichbar" />
  return <StatusBadge state={z.stufe} label={z.text} />
}
